import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Rnd } from "react-rnd";
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  XMarkIcon,
  Bars3Icon,
  Bars2Icon,
  CheckIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ArrowLeftIcon,
  CursorArrowRaysIcon,
  HandRaisedIcon,
  Squares2X2Icon,
  SparklesIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { usePdfViewer } from "../../hooks/usePdfViewer";
import { useFieldMovement } from "../../hooks/useFieldMovement";
import PdfFloatingNav from "./PdfFloatingNav";
import cn from "classnames";
import Button from "../common/Button";
import Input from "../common/Input";
import Tooltip from "../common/Tooltip";
import { FormTemplate, FormFieldDefinition, FormSection } from "../../db/schema";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  resolveFormPdfUrl,
  aiAuditErrorMessage,
  AutoFieldsProposal,
} from "../../services/pdfService";
import ReferencePdfPicker from "./ReferencePdfPicker";
import { FORMS_CHANGED_EVENT, startFieldAudit } from "../../services/formUploadService";
import AiFieldReview from "./AiFieldReview";
import CircleOptionsEditor from "./CircleOptionsEditor";
import { ProcessingCard } from "./TemplateProcessingNotice";
import { buildGroups, composeResult, ReviewGroup, ReviewProposal } from "./fieldReviewLogic";
import TooltipIcon from "../common/TooltipIcon";
import TextArea from "../Settings/TextArea";
import { useFormDesigner } from "../../context/FormDesignerContext";
import { useMarqueeSelection } from "../../hooks/useMarqueeSelection";
import { useFieldAlignment } from "../../hooks/useFieldAlignment";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import { useSnapGuides } from "../../hooks/useSnapGuides";
import SnapGuideLines from "../common/SnapGuideLines";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface FormDesignerProps {
  template: FormTemplate;
  initialFields: FormFieldDefinition[];
  initialSections?: FormSection[];
  onFieldsChange?: (fields: FormFieldDefinition[]) => void;
  onSave: (fields: FormFieldDefinition[], sections: FormSection[]) => void | Promise<void>;
  onCancel: () => void;
  // Base path of the template API, used by "Auto-detect fields". Defaults to
  // personal templates; the agency manager passes its own.
  templatesApiBase?: string;
  // Where this designer lives for a template id (the tray links back to it).
  // Defaults to the officer's route.
  editPath?: (templateId: string) => string;
  // An Auto-detect run finished earlier and left suggestions: open the review
  // as soon as the designer loads.
  pendingSuggestions?: boolean;
  // The server is running the AI on this form (an Auto-detect run, or a new
  // upload's field pass): the designer shows, blurred and locked, with progress
  // and Cancel. Nothing navigates for the officer.
  lockedByServer?: boolean;
  // Where "Back to forms" goes from the locked card.
  backPath?: string;
  // Replaces navigating to `backPath` (the staff portal closes its editor).
  onBack?: () => void;
  // The endpoint that signs a link to this template's PDF. Defaults to the
  // officer's; staff and agency editors use their own (see agencyFormTemplateRoutes).
  viewUrlEndpoint?: string;
  // Where the review card renders (inside a modal <dialog> it must live in the
  // dialog). Defaults to <body>.
  portalContainer?: HTMLElement | null;
}

const FIELD_TYPE_CONFIG: Record<
  string,
  {
    icon: React.ReactNode;
    label: string;
    description: string;
    defaultSize: { w: number; h: number };
  }
> = {
  text: {
    icon: <Bars3Icon className="h-5 w-5" />,
    label: "Text",
    description:
      "Single-line text input for short answers like names, badge numbers, or addresses",
    defaultSize: { w: 200, h: 24 },
  },
  checkbox: {
    icon: <CheckIcon className="h-5 w-5" />,
    label: "Checkbox",
    description: "A checkable box for yes/no or select-all-that-apply options",
    defaultSize: { w: 18, h: 18 },
  },
  circle: {
    icon: <ViewfinderCircleIcon className="h-5 w-5" />,
    label: "Circle",
    description:
      "A printed list of words the filler circles, such as 'Arrestee Passenger Victim'. Add the words as options, then mark where each one is printed",
    defaultSize: { w: 160, h: 36 },
  },
};

type Box = { x: number; y: number; width: number; height: number };

// A circle field's `rect` is the box around its option rects. When the field is
// moved or resized, the option rects move and scale with it.
function retargetOptionRects(f: FormFieldDefinition, next: Box): FormFieldDefinition {
  if (!f.optionRects?.length) return { ...f, rect: next };
  const sx = f.rect.width ? next.width / f.rect.width : 1;
  const sy = f.rect.height ? next.height / f.rect.height : 1;
  return {
    ...f,
    rect: next,
    optionRects: f.optionRects.map((r) => ({
      x: next.x + (r.x - f.rect.x) * sx,
      y: next.y + (r.y - f.rect.y) * sy,
      width: r.width * sx,
      height: r.height * sy,
    })),
  };
}

// The bounding box of the placed option rects (what the field's rect becomes).
function boundsOf(rects: Box[], pad = 2): Box {
  const x = Math.min(...rects.map((r) => r.x)) - pad;
  const y = Math.min(...rects.map((r) => r.y)) - pad;
  return {
    x,
    y,
    width: Math.max(...rects.map((r) => r.x + r.width)) + pad - x,
    height: Math.max(...rects.map((r) => r.y + r.height)) + pad - y,
  };
}

// Sidebar field list organized by sections
interface SidebarFieldListProps {
  fields: FormFieldDefinition[];
  sections: FormSection[];
  selectedFieldIds: Set<string>;
  onSelectField: (id: string) => void;
  onToggleField: (id: string) => void;
  onDeleteField: (id: string) => void;
  onUpdateField: (id: string, updates: Partial<FormFieldDefinition>) => void;
  dragFieldId: string | null;
  setDragFieldId: (id: string | null) => void;
  dragOverSection: string | null;
  setDragOverSection: (section: string | null) => void;
  onBulkUpdateSection: (
    fieldIds: Set<string>,
    section: string | undefined,
  ) => void;
  onAddSection: () => void;
  onRemoveSection: (section: string) => void;
  onUpdateSectionContext: (sectionName: string, context: string | undefined) => void;
  onReorderFieldInSection: (fieldId: string, section: string, direction: "up" | "down") => void;
  onDropFieldAtPosition: (fieldId: string, targetSection: string, targetOrder: number) => void;
  hoveredFieldId: string | null;
  onHoverField: (id: string | null) => void;
  hoveredSection: string | null;
  onHoverSection: (section: string | null) => void;
  onReorderSections: (fromIndex: number, toIndex: number) => void;
  currentPage: number;
  onNavigateToPage: (page: number) => void;
  collapsedSections: Set<string>;
  onToggleCollapse: (section: string) => void;
  // The suggestion under review (shown green, like inserted text in the report
  // editor): its fields and the section they belong to.
  highlightedFieldIds?: Set<string>;
  highlightedSection?: string | null;
}

const SidebarFieldList: React.FC<SidebarFieldListProps> = ({
  fields,
  sections,
  selectedFieldIds,
  onSelectField,
  onToggleField,
  onDeleteField,
  onUpdateField,
  dragFieldId,
  setDragFieldId,
  dragOverSection,
  setDragOverSection,
  onBulkUpdateSection,
  onAddSection,
  onRemoveSection,
  onUpdateSectionContext,
  onReorderFieldInSection,
  onDropFieldAtPosition,
  hoveredFieldId,
  onHoverField,
  hoveredSection,
  onHoverSection,
  onReorderSections,
  currentPage,
  onNavigateToPage,
  collapsedSections,
  onToggleCollapse,
  highlightedFieldIds,
  highlightedSection,
}) => {
  const [dropIndicator, setDropIndicator] = useState<{ section: string; index: number } | null>(null);
  const [dragSectionName, setDragSectionName] = useState<string | null>(null);
  const [sectionDropIndex, setSectionDropIndex] = useState<number | null>(null);

  // Group fields by section
  const grouped: Record<string, FormFieldDefinition[]> = {};
  const sectionNames = sections.map((s) => s.name);
  for (const sec of sections) {
    grouped[sec.name] = [];
  }
  for (const field of fields) {
    const section = field.section || "Uncategorized";
    if (!grouped[section]) grouped[section] = [];
    grouped[section].push(field);
  }
  if (!grouped["Uncategorized"]) grouped["Uncategorized"] = [];

  const orderedSections = [
    ...sectionNames.filter((s) => s in grouped),
    ...Object.keys(grouped).filter(
      (s) => s !== "Uncategorized" && !sectionNames.includes(s),
    ),
    "Uncategorized",
  ];

  const handleDragOver = (e: React.DragEvent, section: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverSection(section);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    const currentTarget = e.currentTarget as HTMLElement;
    if (!currentTarget.contains(relatedTarget)) {
      setDragOverSection(null);
    }
  };

  const handleDrop = (e: React.DragEvent, targetSection: string) => {
    e.preventDefault();
    if (!dragFieldId) return;
    const newSection =
      targetSection === "Uncategorized" ? undefined : targetSection;
    // If the dragged field is part of a multi-selection, move all selected fields
    if (selectedFieldIds.has(dragFieldId) && selectedFieldIds.size > 1) {
      onBulkUpdateSection(selectedFieldIds, newSection);
    } else if (dropIndicator && dropIndicator.section === targetSection) {
      // Single field: positional reorder
      onDropFieldAtPosition(dragFieldId, targetSection, dropIndicator.index);
    } else {
      // Single field: section change
      onUpdateField(dragFieldId, { section: newSection });
    }
    setDragFieldId(null);
    setDragOverSection(null);
    setDropIndicator(null);
  };

  // Sections that can be reordered (everything except Uncategorized)
  const draggableSections = orderedSections.filter((s) => s !== "Uncategorized");

  return (
    <div className="flex-1 overflow-auto">
      {orderedSections.map((section) => {
        const sectionFields = (grouped[section] || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
        const isCollapsed = collapsedSections.has(section);
        const isDropTarget = dragOverSection === section;
        const isDragging = !!dragFieldId;
        const isDraggableSection = section !== "Uncategorized";
        const draggableIdx = draggableSections.indexOf(section);

        return (
          <div
            key={section}
            className={cn(
              "border-b border-base-200 dark:border-base-300 transition-colors",
              isDropTarget && !dragSectionName && "bg-blue-50 dark:bg-blue-900/40",
            )}
            onDragOver={(e) => {
              if (dragSectionName) return;
              handleDragOver(e, section);
            }}
            onDragLeave={(e) => {
              if (dragSectionName) return;
              handleDragLeave(e);
            }}
            onDrop={(e) => {
              if (dragSectionName) return;
              handleDrop(e, section);
            }}
          >
            {/* Section drop indicator */}
            {dragSectionName && isDraggableSection && sectionDropIndex === draggableIdx && (
              <div className="h-0.5 bg-blue-500 mx-2 rounded-full" />
            )}

            {/* Section header */}
            <div
              draggable={isDraggableSection && !dragFieldId}
              onDragStart={(e) => {
                if (!isDraggableSection) return;
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/section", section);
                setDragSectionName(section);
              }}
              onDragEnd={() => {
                setDragSectionName(null);
                setSectionDropIndex(null);
              }}
              onDragOver={(e) => {
                if (!dragSectionName || !isDraggableSection) return;
                e.preventDefault();
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                const midY = rect.top + rect.height / 2;
                const dropIdx = e.clientY < midY ? draggableIdx : draggableIdx + 1;
                setSectionDropIndex(dropIdx);
              }}
              onDrop={(e) => {
                if (!dragSectionName) return;
                e.preventDefault();
                e.stopPropagation();
                if (sectionDropIndex !== null) {
                  const fromIdx = draggableSections.indexOf(dragSectionName);
                  if (fromIdx !== -1 && sectionDropIndex !== fromIdx && sectionDropIndex !== fromIdx + 1) {
                    onReorderSections(fromIdx, sectionDropIndex > fromIdx ? sectionDropIndex - 1 : sectionDropIndex);
                  }
                }
                setDragSectionName(null);
                setSectionDropIndex(null);
              }}
              className={cn(
                "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
                highlightedSection === section
                  ? "bg-green-200/70 dark:bg-green-700/40"
                  : "bg-base-200 hover:bg-gray-100 dark:hover:bg-base-300",
                isDraggableSection && !dragFieldId && "cursor-grab active:cursor-grabbing",
                dragSectionName === section && "opacity-30",
              )}
              onMouseEnter={() => onHoverSection(section)}
              onMouseLeave={() => onHoverSection(null)}
              onClick={() => onToggleCollapse(section)}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {isCollapsed ? (
                  <ChevronRightIcon className="h-3.5 w-3.5 text-base-content/40 flex-shrink-0" />
                ) : (
                  <ChevronDownIcon className="h-3.5 w-3.5 text-base-content/40 flex-shrink-0" />
                )}
                <span className="text-xs font-semibold text-gray-700 dark:text-base-content/80 truncate">
                  {section}
                </span>
                <span className="text-xs text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {sectionFields.length}
                </span>
                {(() => {
                  const selectedInSection = sectionFields.filter((f) =>
                    selectedFieldIds.has(f.id),
                  ).length;
                  return selectedInSection > 0 ? (
                    <span className="text-xs text-white bg-blue-500 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      {selectedInSection} selected
                    </span>
                  ) : null;
                })()}
              </div>
              {section !== "Uncategorized" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSection(section);
                  }}
                  className="p-0.5 text-gray-300 dark:text-base-content/30 hover:text-red-500 transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Section context */}
            {section !== "Uncategorized" && !isCollapsed && (
              <div className="px-3 py-1.5 border-b border-base-200 dark:border-base-300" onClick={(e) => e.stopPropagation()}>
                <TextArea
                  label=""
                  className="!text-xs !min-h-0 !py-1 !px-2"
                  rows={2}
                  placeholder="AI context (e.g., 'Extract details about the subject's physical resistance')"
                  value={sections.find((s) => s.name === section)?.context || ""}
                  onChange={(e) =>
                    onUpdateSectionContext(
                      section,
                      e.target.value || undefined,
                    )
                  }
                />
              </div>
            )}

            {/* Section fields */}
            {!isCollapsed && (
              <div className="py-1">
                {sectionFields.length === 0 ? (
                  <p
                    className={cn(
                      "text-xs text-center py-3",
                      isDragging ? "text-blue-400" : "text-gray-300 dark:text-base-content/30",
                    )}
                  >
                    {isDragging ? "Drop here" : "No fields"}
                  </p>
                ) : (
                  sectionFields.map((field, idx) => (
                    <React.Fragment key={field.id}>
                      {/* Drop indicator line */}
                      {dropIndicator?.section === section && dropIndicator.index === idx && (
                        <div className="h-0.5 bg-blue-500 mx-3 rounded-full" />
                      )}
                      <div
                        data-field-row-id={field.id}
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", field.id);
                          setDragFieldId(field.id);
                        }}
                        onDragEnd={() => {
                          setDragFieldId(null);
                          setDragOverSection(null);
                          setDropIndicator(null);
                        }}
                        onDragOver={(e) => {
                          if (!dragFieldId || dragFieldId === field.id) return;
                          e.preventDefault();
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          const midY = rect.top + rect.height / 2;
                          const dropIdx = e.clientY < midY ? idx : idx + 1;
                          setDropIndicator({ section, index: dropIdx });
                          setDragOverSection(section);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (!dragFieldId) return;
                          // Multi-select: move all selected fields to this section
                          if (selectedFieldIds.has(dragFieldId) && selectedFieldIds.size > 1) {
                            const newSection = section === "Uncategorized" ? undefined : section;
                            onBulkUpdateSection(selectedFieldIds, newSection);
                          } else if (dropIndicator) {
                            onDropFieldAtPosition(dragFieldId, section, dropIndicator.index);
                          }
                          setDragFieldId(null);
                          setDragOverSection(null);
                          setDropIndicator(null);
                        }}
                        onClick={(e) => {
                          if (e.shiftKey) {
                            onToggleField(field.id);
                          } else {
                            onSelectField(field.id);
                          }
                        }}
                        onMouseEnter={() => onHoverField(field.id)}
                        onMouseLeave={() => onHoverField(null)}
                        className={cn(
                          "flex items-center gap-1.5 px-3 py-1.5 text-sm cursor-pointer group transition-all",
                          highlightedFieldIds?.has(field.id)
                            ? "bg-green-200/70 dark:bg-green-700/40 text-green-900 dark:text-green-200"
                            : selectedFieldIds.has(field.id)
                            ? "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                            : hoveredFieldId === field.id || hoveredSection === section
                              ? "bg-blue-50/50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-300"
                              : "hover:bg-base-200 text-gray-700 dark:text-base-content/80",
                          (dragFieldId === field.id || (dragFieldId && selectedFieldIds.has(dragFieldId) && selectedFieldIds.size > 1 && selectedFieldIds.has(field.id))) && "opacity-30",
                        )}
                      >
                        <span className="text-[10px] text-gray-300 dark:text-base-content/30 w-3 text-right flex-shrink-0 font-mono">
                          {field.order}
                        </span>
                        <Bars2Icon className="h-3.5 w-3.5 text-gray-300 dark:text-base-content/30 cursor-grab active:cursor-grabbing flex-shrink-0" />
                        <span className="truncate flex-1 text-xs">
                          {field.label || field.name}
                        </span>
                        {field.required && (
                          <span className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                        )}
                        {field.page !== currentPage && (
                          <span
                            className="text-[9px] text-base-content/40 bg-gray-100 dark:bg-base-200 px-1 py-0.5 rounded flex-shrink-0 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/60 hover:text-blue-500 transition-colors"
                            title={`Go to page ${field.page}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onNavigateToPage(field.page);
                              onSelectField(field.id);
                            }}
                          >
                            p{field.page}
                          </span>
                        )}
                        <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onReorderFieldInSection(field.id, section, "up");
                            }}
                            disabled={idx === 0}
                            className="p-0 text-gray-300 dark:text-base-content/30 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-0 leading-none"
                          >
                            <ChevronUpIcon className="h-3 w-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onReorderFieldInSection(field.id, section, "down");
                            }}
                            disabled={idx === sectionFields.length - 1}
                            className="p-0 text-gray-300 dark:text-base-content/30 hover:text-gray-600 dark:hover:text-base-content/70 disabled:opacity-0 leading-none"
                          >
                            <ChevronDownIcon className="h-3 w-3" />
                          </button>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteField(field.id);
                          }}
                          className="p-0.5 text-gray-300 dark:text-base-content/30 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                      {/* Drop indicator at end of list */}
                      {idx === sectionFields.length - 1 &&
                        dropIndicator?.section === section &&
                        dropIndicator.index === sectionFields.length && (
                          <div className="h-0.5 bg-blue-500 mx-3 rounded-full" />
                        )}
                    </React.Fragment>
                  ))
                )}
              </div>
            )}
            {/* Section drop indicator after last draggable section */}
            {dragSectionName && isDraggableSection &&
              draggableIdx === draggableSections.length - 1 &&
              sectionDropIndex === draggableSections.length && (
                <div className="h-0.5 bg-blue-500 mx-2 rounded-full" />
              )}
          </div>
        );
      })}

      {/* Add Section button */}
      <button
        onClick={onAddSection}
        className="w-full py-3 text-xs text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80 hover:bg-base-200 transition-colors flex items-center justify-center gap-1 border-b border-base-200 dark:border-base-300"
      >
        <PlusIcon className="h-3.5 w-3.5" />
        Add Section
      </button>
    </div>
  );
};

const FormDesigner: React.FC<FormDesignerProps> = ({
  template,
  initialFields,
  initialSections = [],
  onFieldsChange,
  onSave,
  onCancel,
  templatesApiBase = "/api/forms/templates",
  editPath = (id) => `/forms/${id}/edit`,
  pendingSuggestions = false,
  lockedByServer = false,
  backPath = "/forms",
  onBack,
  viewUrlEndpoint,
  portalContainer,
}) => {
  const { setIsDesignerOpen } = useFormDesigner();

  // Signal to Menu that designer is open
  useEffect(() => {
    setIsDesignerOpen(true);
    return () => setIsDesignerOpen(false);
  }, [setIsDesignerOpen]);

  const [pdfFileUrl, setPdfFileUrl] = useState<string>("");
  useEffect(() => {
    let cancelled = false;
    resolveFormPdfUrl(template.original_pdf_url, viewUrlEndpoint).then((u) => {
      if (!cancelled) setPdfFileUrl(u);
    });
    return () => {
      cancelled = true;
    };
  }, [template.original_pdf_url, viewUrlEndpoint]);

  const { state: fields, setState: setFields, undo: handleUndo, redo: handleRedo, canUndo, canRedo, reset: resetFields } = useUndoRedo<FormFieldDefinition[]>(initialFields);

  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const editingInputRef = useRef<HTMLInputElement>(null);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [selectedTool, setSelectedTool] = useState<string | null>("select");
  // True while the AI is finding and grouping fields: the whole editor is
  // locked (see the overlay). Read by the global key handler.
  const aiBusyRef = useRef(false);
  const designerRootRef = useRef<HTMLDivElement>(null);

  const pdf = usePdfViewer({
    storageKey: "formDesignerZoom",
    minScale: 0.5,
    maxScale: 2,
    zoomStep: 1.1,
    selectedTool,
  });
  const [sections, setSections] = useState<FormSection[]>(initialSections);
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionContext, setNewSectionContext] = useState("");
  const [sectionToDelete, setSectionToDelete] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [sidebarTab, setSidebarTab] = useState<"fields" | "properties">(
    "fields",
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const sidebarPanel = useResizablePanel({
    storageKey: "pnai-formDesignerSidebarWidth",
    defaultWidth: 320,
    minWidth: 240,
    maxWidth: 640,
    side: "left",
    maxViewportFraction: 0.5,
  });

  // Drag-and-drop state for field list
  const [dragFieldId, setDragFieldId] = useState<string | null>(null);
  const [dragOverSection, setDragOverSection] = useState<string | null>(null);

  // Clipboard for copy/paste
  const clipboardRef = useRef<FormFieldDefinition[]>([]);

  // Get fields for current page (defined early for keyboard shortcut deps)
  const currentPageFields = fields.filter((f) => f.page === pdf.currentPage);

  // Marquee selection
  const marquee = useMarqueeSelection({
    canvasRef: pdf.canvasRef,
    scale: pdf.scale,
    getCanvasCoords: pdf.getCanvasCoords,
    getCurrentPageFields: useCallback(
      () => currentPageFields.map((f) => ({ id: f.id, rect: f.rect })),
      [currentPageFields],
    ),
  });
  const { selectedFieldIds, setSelectedFieldIds } = marquee;

  // Clear selection on page change
  useEffect(() => {
    marquee.clearSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf.currentPage]);

  // When a single field is selected on the canvas and the Fields sidebar tab
  // is active, scroll its row into view (expanding its section if collapsed).
  useEffect(() => {
    if (sidebarTab !== "fields" || selectedFieldIds.size !== 1) return;
    const fieldId = Array.from(selectedFieldIds)[0];
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;
    const sectionName = field.section || "Uncategorized";

    if (collapsedSections.has(sectionName)) {
      setCollapsedSections((prev) => {
        const next = new Set(prev);
        next.delete(sectionName);
        return next;
      });
    }

    // Wait a tick for any section expansion to reflow before scrolling.
    const id = window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-field-row-id="${fieldId}"]`,
      );
      el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedFieldIds, sidebarTab, fields, collapsedSections]);

  // Keyboard shortcuts
  useEffect(() => {
    const isInputFocused = () => {
      const el = document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (aiBusyRef.current) return;
      // Undo/Redo
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }

      // Ctrl/Cmd+C copy selected fields
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "c" &&
        selectedFieldIds.size > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        clipboardRef.current = fields.filter((f) => selectedFieldIds.has(f.id));
        return;
      }

      // Ctrl/Cmd+V paste copied fields
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "v" &&
        clipboardRef.current.length > 0 &&
        !isInputFocused()
      ) {
        e.preventDefault();
        const offset = 15;
        const newFields = clipboardRef.current.map((f) => ({
          ...f,
          id: `field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          name: `${f.name}_copy`,
          page: pdf.currentPage,
          rect: f.rect
            ? { ...f.rect, x: f.rect.x + offset, y: f.rect.y + offset }
            : f.rect,
        }));
        setFields((prev) => reorderBySections([...prev, ...newFields]));
        setSelectedFieldIds(new Set(newFields.map((f) => f.id)));
        // Shift clipboard offset so repeated pastes cascade
        clipboardRef.current = newFields;
        return;
      }

      // Ctrl/Cmd+A select all on current page
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key === "a" &&
        selectedTool === "select" &&
        !isInputFocused()
      ) {
        e.preventDefault();
        marquee.selectAll();
        return;
      }

      // Escape clears selection
      if (e.key === "Escape") {
        marquee.clearSelection();
        setSelectedTool("select");
        return;
      }

      // Delete/Backspace deletes all selected fields
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        selectedFieldIds.size > 0 &&
        !editingLabelId &&
        !isInputFocused()
      ) {
        e.preventDefault();
        setFields((prev) => prev.filter((f) => !selectedFieldIds.has(f.id)));
        setSelectedFieldIds(new Set());
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [
    handleUndo,
    handleRedo,
    selectedFieldIds,
    editingLabelId,
    selectedTool,
    currentPageFields,
    pdf.currentPage,
    fields,
    setFields,
  ]);

  // Focus container on mount so keyboard shortcuts work immediately
  useEffect(() => {
    pdf.containerRef.current?.focus();
  }, [pdf.containerRef]);

  // Track unsaved changes, guard navigation, handle cancel
  const { hasChanges, markSaved, handleCancel } = useUnsavedChanges({
    currentState: fields,
    initialState: initialFields,
    onCancel,
  });

  // AI audit. The server returns a proposal and saves nothing; the officer
  // approves it group by group (AiFieldReview) and only then is anything saved.
  // Unsaved edits are saved first because the server audits the stored copy.
  const [aiStep, setAiStep] = useState<string | null>(null);
  // After the job is handed to the server this card stays until the lists
  // report the form as locked, then the full "still reading" card (progress,
  // Cancel) takes over. A time limit means it can never stay stuck.
  const [handedOff, setHandedOff] = useState(false);
  useEffect(() => {
    if (!handedOff) return;
    if (lockedByServer) {
      setAiStep(null);
      setHandedOff(false);
      return;
    }
    const t = setTimeout(() => {
      setAiStep(null);
      setHandedOff(false);
    }, 20000);
    return () => clearTimeout(t);
  }, [handedOff, lockedByServer]);
  const locked = !!aiStep || lockedByServer;
  aiBusyRef.current = locked;
  // `inert` makes the designer unclickable, unfocusable and unreachable by
  // keyboard, so nothing can change under the AI while it works. Everything
  // inside the root is locked except the overlay itself, whose buttons (Back,
  // Cancel) have to stay usable.
  useEffect(() => {
    const root = designerRootRef.current;
    if (!root) return;
    Array.from(root.children).forEach((child) => {
      (child as HTMLElement & { inert?: boolean }).inert = locked && !child.hasAttribute("data-lock-overlay");
    });
  }, [locked]);
  const [showAiConfirm, setShowAiConfirm] = useState(false);
  // The suggestion on screen during review: painted green on the form and in
  // the field list, the way the report editor marks inserted text.
  const [reviewHighlight, setReviewHighlight] = useState<{ ids: Set<string>; section: string | null } | null>(null);
  const [review, setReview] = useState<{
    before: FormFieldDefinition[];
    beforeSections: FormSection[];
    proposal: ReviewProposal;
    groups: ReviewGroup[];
  } | null>(null);

  // Forget the suggestions on the server (accepted, discarded, or expired) and
  // let the lists drop their "ready to review" note.
  const dismissSuggestions = () => {
    axios
      .delete(`${templatesApiBase}/${template.id}/auto-fields`)
      .catch(() => {})
      .finally(() => window.dispatchEvent(new Event(FORMS_CHANGED_EVENT)));
  };

  // Hand the form to the server and leave: it reads the form in the background
  // (the form is locked meanwhile) and keeps the suggestions until they are
  // reviewed. Unsaved edits are saved first because the server audits the
  // stored copy.
  const [aiReferenceFile, setAiReferenceFile] = useState<File | null>(null);
  const runAutoDetect = async () => {
    if (aiStep || !pdfFileUrl) return;
    try {
      setAiStep("Reading the form...");
      if (hasChanges) {
        await onSave(fields, sections);
        markSaved();
      }
      const pdfBytes = await (await fetch(pdfFileUrl)).arrayBuffer();
      await startFieldAudit({
        templateId: template.id,
        name: template.name,
        pdf: pdfBytes,
        apiBase: templatesApiBase,
        openPath: editPath(template.id),
        onStep: setAiStep,
        referenceFile: aiReferenceFile ?? undefined,
      });
      setAiReferenceFile(null);
      // Stay locked: the overlay switches to the full "still reading" card (with
      // Cancel) as soon as the lists see the form is processing. Nothing
      // navigates for the officer.
      setHandedOff(true);
      toast.success("Reading the form in the background. You can leave this page; it opens with suggestions when done.");
    } catch (error) {
      console.error("AI field analysis failed:", error);
      toast.error(`Could not auto-detect fields (${aiAuditErrorMessage(error)})`);
      setAiStep(null);
    }
  };

  // Suggestions from an earlier run: fetch them from the server and open the
  // review. If they expired (the server keeps them a day), say so.
  const openedSuggestionsRef = useRef(false);
  useEffect(() => {
    // Wait for the PDF to load first: the review jumps between pages.
    if (!pendingSuggestions || openedSuggestionsRef.current || pdf.numPages < 1) return;
    openedSuggestionsRef.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${templatesApiBase}/${template.id}/auto-fields`);
        const proposal = data?.result as AutoFieldsProposal | undefined;
        if (data?.status !== "done" || !proposal) {
          toast("Those suggestions expired. Run Auto-detect fields again.");
          dismissSuggestions();
          return;
        }
        const groups = buildGroups(fields, sections, proposal);
        if (!groups.length) {
          toast.success("No suggestions. This form looks complete.");
          dismissSuggestions();
          return;
        }
        setReview({ before: fields, beforeSections: sections, proposal, groups });
      } catch (error) {
        console.error("Could not load AI suggestions:", error);
        openedSuggestionsRef.current = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSuggestions, pdf.numPages]);

  // Always confirm first: it is also where an optional completed example is added.
  const handleAutoDetect = () => {
    if (aiStep || !pdfFileUrl) return;
    setShowAiConfirm(true);
  };

  // Show a step live: the approved groups plus the one on screen, with the
  // step's new fields selected and its page in view.
  const previewReviewStep = (accepted: Set<string>, current: ReviewGroup) => {
    if (!review) return;
    const result = composeResult(review.before, review.beforeSections, review.proposal, review.groups, accepted);
    resetFields(result.fields);
    setSections(result.sections);
    setSelectedFieldIds(new Set());
    setReviewHighlight({
      ids: new Set([...current.added.map((f) => f.id), ...current.changed.map((c) => c.after.id)]),
      section: current.key || null,
    });
    // Show the list with the suggested section open
    setSidebarTab("fields");
    if (current.key) {
      setCollapsedSections((prev) => {
        if (!prev.has(current.key)) return prev;
        const next = new Set(prev);
        next.delete(current.key);
        return next;
      });
    }
    pdf.goToPage(current.added[0]?.page ?? current.changed[0]?.after.page ?? current.firstPage);
  };

  const finishReview = async (accepted: Set<string>) => {
    if (!review) return;
    // Nothing approved: put back exactly what was there (no reordering noise).
    if (!accepted.size) return discardReview();
    dismissSuggestions();
    const result = composeResult(review.before, review.beforeSections, review.proposal, review.groups, accepted);
    resetFields(result.fields);
    setSections(result.sections);
    setSelectedFieldIds(new Set());
    setReviewHighlight(null);
    setReview(null);
    try {
      await onSave(result.fields, result.sections);
      markSaved(result.fields);
      const added = result.fields.filter((f) => f.source === "ai").length;
      toast.success(`Applied ${accepted.size} of ${review.groups.length} groups (${added} new fields)`);
    } catch (error) {
      console.error("Saving AI field suggestions failed:", error);
      toast.error("Could not save the accepted suggestions. Press Save Template to retry.");
    }
  };

  function discardReview() {
    if (!review) return;
    dismissSuggestions();
    resetFields(review.before);
    setSections(review.beforeSections);
    setSelectedFieldIds(new Set());
    setReviewHighlight(null);
    setReview(null);
  }

  // Notify parent of field changes
  useEffect(() => {
    onFieldsChange?.(fields);
  }, [fields, onFieldsChange]);

  // Destructure commonly used pdf viewer values
  const { scale } = pdf;

  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      setHoveredFieldId(null);
      setHoveredSection(null);

      // Blur the editing input to trigger its onBlur save handler
      if (editingInputRef.current) {
        editingInputRef.current.blur();
      }

      // Pan check (handles button check, .react-draggable skip, space/pan tool)
      if (pdf.handleContainerMouseDown(e)) return;

      // Select mode: start marquee from anywhere in the container (grey bg or canvas)
      if (selectedTool === "select") {
        marquee.startMarquee(e);
        return;
      }
    },
    [selectedTool, pdf, marquee],
  );

  // Drawing state for click-and-drag field creation
  const [drawStart, setDrawStart] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [drawCurrent, setDrawCurrent] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Marking where a circle field's options are printed: the next drag on the
  // page sets that option's rect, then moves on to the next option.
  const [placingOption, setPlacingOption] = useState<{
    fieldId: string;
    index: number;
    // true: place just this one option, then stop
    single?: boolean;
  } | null>(null);
  useEffect(() => {
    if (!placingOption) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPlacingOption(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placingOption]);

  // Shared field rect callbacks for movement + alignment hooks
  const getFieldRect = useCallback(
    (fieldId: string) => {
      const f = fields.find((f) => f.id === fieldId);
      return f ? f.rect : null;
    },
    [fields],
  );
  const onMoveFields = useCallback(
    (updates: Array<{ id: string; rect: { x: number; y: number; width: number; height: number } }>) => {
      setFields((prev) => {
        const map = new Map(updates.map((u) => [u.id, u.rect]));
        return prev.map((f) => (map.has(f.id) ? retargetOptionRects(f, map.get(f.id)!) : f));
      });
    },
    [setFields],
  );

  // Figma-style alignment snapping with live guide lines. The selection is
  // excluded from targets so group drags don't snap to co-moving fields.
  const snap = useSnapGuides({
    getTargetRects: useCallback(
      () => currentPageFields.map((f) => ({ id: f.id, rect: f.rect })),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [fields, pdf.currentPage],
    ),
    getExcludedIds: useCallback(() => selectedFieldIds, [selectedFieldIds]),
    pageWidth: pdf.pdfDimensions.width,
    pageHeight: pdf.pdfDimensions.height,
    scale,
  });

  // Field movement (drag + resize) via shared hook
  const fieldMovement = useFieldMovement({
    scale,
    getFieldRect,
    onMoveFields,
    onResizeField: useCallback(
      (fieldId, rect) => {
        setFields((prev) =>
          prev.map((f) => (f.id === fieldId ? retargetOptionRects(f, rect) : f)),
        );
      },
      [setFields],
    ),
    selectedFieldIds,
    snapRect: snap.snapRect,
    onLiveDrag: snap.previewSnap,
    onDragEnd: snap.clearGuides,
    snapResizeRect: snap.snapResizeRect,
    onLiveResize: snap.previewResizeSnap,
  });

  // Field alignment for multi-select
  const alignment = useFieldAlignment({
    getFieldRect,
    onMoveFields,
    selectedFieldIds,
  });

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!pdf.canvasRef.current || e.button !== 0) return;

      // Field creation mode (only on canvas)
      if ((selectedTool && FIELD_TYPE_CONFIG[selectedTool]) || placingOption) {
        e.preventDefault();
        const coords = pdf.getCanvasCoords(e);
        setDrawStart(coords);
        setDrawCurrent(coords);
      }
    },
    [selectedTool, pdf, placingOption],
  );

  const handleCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (drawStart) {
        setDrawCurrent(pdf.getCanvasCoords(e));
      }
    },
    [drawStart, pdf],
  );

  const handleCanvasMouseUp = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Marking one option of a circle field
      if (placingOption && drawStart) {
        const end = pdf.getCanvasCoords(e);
        const dragW = Math.abs(end.x - drawStart.x);
        const dragH = Math.abs(end.y - drawStart.y);
        const dragged = dragW > 4 && dragH > 4;
        const optRect: Box = dragged
          ? { x: Math.min(drawStart.x, end.x), y: Math.min(drawStart.y, end.y), width: dragW, height: dragH }
          : { x: end.x - 20, y: end.y - 7, width: 40, height: 14 };
        const { fieldId, index, single } = placingOption;
        setFields((prev) =>
          prev.map((f) => {
            if (f.id !== fieldId) return f;
            const rects = [...(f.optionRects || [])];
            rects[index] = optRect;
            const placed = rects.filter(Boolean) as Box[];
            return { ...f, page: pdf.currentPage, optionRects: rects, rect: boundsOf(placed) };
          }),
        );
        const total = fields.find((f) => f.id === fieldId)?.options?.length ?? 0;
        setPlacingOption(!single && index + 1 < total ? { fieldId, index: index + 1 } : null);
        setDrawStart(null);
        setDrawCurrent(null);
        return;
      }

      // Field creation completion
      if (selectedTool && FIELD_TYPE_CONFIG[selectedTool] && drawStart) {
        const end = pdf.getCanvasCoords(e);
        const config = FIELD_TYPE_CONFIG[selectedTool];

        const x = Math.min(drawStart.x, end.x);
        const y = Math.min(drawStart.y, end.y);
        const dragW = Math.abs(end.x - drawStart.x);
        const dragH = Math.abs(end.y - drawStart.y);

        const minDrag = 10;
        const isCheckbox = selectedTool === "checkbox";
        const storedCheckboxSize = parseInt(localStorage.getItem("formDefaults:checkboxSize") || "") || config.defaultSize.w;
        const defaultW = isCheckbox ? storedCheckboxSize : config.defaultSize.w;
        const defaultH = isCheckbox ? storedCheckboxSize : config.defaultSize.h;
        const width = dragW > minDrag ? dragW : defaultW;
        const height = dragH > minDrag ? dragH : defaultH;
        const finalX = dragW > minDrag ? x : drawStart.x;
        const finalY = dragH > minDrag ? y : drawStart.y;

        const storedFontSize = parseInt(localStorage.getItem("formDefaults:fontSize") || "") || 10;
        const storedLineHeight = parseFloat(localStorage.getItem("formDefaults:lineHeight") || "") || 1.2;
        const storedMultiline = localStorage.getItem("formDefaults:multiline");
        const isMultiline = storedMultiline === null ? true : storedMultiline === "true";
        const fieldType = selectedTool === "text" && isMultiline ? "textarea" : selectedTool;
        const newField: FormFieldDefinition = {
          id: `field_${Date.now()}`,
          name: `field_${fields.length + 1}`,
          label: `${config.label} ${fields.length + 1}`,
          type: fieldType as any,
          required: false,
          page: pdf.currentPage,
          rect: { x: finalX, y: finalY, width, height },
          order: 0,
          source: "manual",
          ...(isCheckbox || fieldType === "circle" ? {} : { fontSize: storedFontSize }),
          ...(fieldType === "circle" ? { options: [], optionRects: [], multiple: false } : {}),
          ...(fieldType === "textarea" ? { lineHeight: storedLineHeight } : {}),
        };

        if (isCheckbox && dragW > minDrag) {
          localStorage.setItem("formDefaults:checkboxSize", String(Math.round(width)));
        }

        setFields((prev) => reorderBySections([...prev, newField]));
        setSelectedFieldIds(new Set([newField.id]));
        setSelectedTool("select");
        setDrawStart(null);
        setDrawCurrent(null);
      }
    },
    [
      selectedTool,
      drawStart,
      fields,
      fields.length,
      pdf.currentPage,
      pdf,
      setFields,
      placingOption,
    ],
  );

  // Draw preview rectangle (field creation)
  const drawPreview =
    drawStart && drawCurrent && ((selectedTool && FIELD_TYPE_CONFIG[selectedTool]) || placingOption)
      ? {
          left: Math.min(drawStart.x, drawCurrent.x) * scale,
          top: Math.min(drawStart.y, drawCurrent.y) * scale,
          width: Math.abs(drawCurrent.x - drawStart.x) * scale,
          height: Math.abs(drawCurrent.y - drawStart.y) * scale,
        }
      : null;

  // Delete field
  const handleDeleteField = (fieldId: string) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    setSelectedFieldIds((prev) => {
      if (prev.has(fieldId)) {
        const next = new Set(prev);
        next.delete(fieldId);
        return next;
      }
      return prev;
    });
  };

  // Update field properties
  // Recalculate per-section order (1-based within each section)
  const reorderBySections = useCallback((fieldList: FormFieldDefinition[]) => {
    const counters: Record<string, number> = {};
    return fieldList.map((f) => {
      const sec = f.section || "";
      counters[sec] = (counters[sec] || 0) + 1;
      return { ...f, order: counters[sec] };
    });
  }, []);

  const handleFieldUpdate = (
    fieldId: string,
    updates: Partial<FormFieldDefinition>,
  ) => {
    setFields((prev) => {
      const updated = prev.map((f) => (f.id === fieldId ? { ...f, ...updates } : f));
      return "section" in updates ? reorderBySections(updated) : updated;
    });
  };

  // Derive primary selection for properties panel
  const primarySelectedId =
    selectedFieldIds.size === 1 ? Array.from(selectedFieldIds)[0] : null;
  const selectedField = fields.find((f) => f.id === primarySelectedId);

  // Bulk section assign flag for modal
  const [bulkSectionAssign, setBulkSectionAssign] = useState(false);

  return (
    <div ref={designerRootRef} className="relative flex-1 min-w-0 h-screen flex flex-col bg-base-200">
      {locked && (
        <div
          data-lock-overlay
          className="absolute inset-0 z-[60] flex items-center justify-center bg-base-100/60 backdrop-blur-[1px] cursor-wait"
        >
          {aiStep ? (
            <div className="rounded-xl border border-base-300 bg-base-100 shadow-xl px-6 py-5 text-center max-w-sm">
              <span className="loading loading-spinner loading-md text-blue-500" />
              <p className="mt-3 mb-0 text-sm font-semibold text-base-content">{aiStep}</p>
              <p className="mt-1 mb-0 text-xs text-base-content/60">
                The form is locked until this finishes, so nothing changes while the AI works.
              </p>
            </div>
          ) : (
            <div className="cursor-default">
              <ProcessingCard name={template.name} backPath={backPath} onBack={onBack} templateId={template.id} apiBase={templatesApiBase} />
            </div>
          )}
        </div>
      )}
      {/* Header */}
      <div className="bg-base-100 border-b border-base-300 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={handleCancel}>
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {hasChanges ? "Cancel" : "Back"}
          </Button>
          <div>
            <h2 className="font-bold">Form Designer</h2>
            <p className="text-xs text-base-content/60">
              Click to place fields on the PDF
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-sm text-base-content/60">
            {`${fields.length} fields`}
          </span>
          <button
            onClick={handleUndo}
            disabled={!canUndo}
            title="Undo (Ctrl+Z)"
            className="p-1 rounded hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity duration-200"
          >
            <ArrowUturnLeftIcon className="h-5 w-5" />
          </button>
          <button
            onClick={handleRedo}
            disabled={!canRedo}
            title="Redo (Ctrl+Shift+Z)"
            className="p-1 rounded hover:bg-base-200 disabled:opacity-30 disabled:cursor-not-allowed transition-opacity duration-200"
          >
            <ArrowUturnRightIcon className="h-5 w-5" />
          </button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!!aiStep || !pdfFileUrl}
            onClick={handleAutoDetect}
            className="mr-1"
          >
            {aiStep ? (
              <span className="loading loading-spinner loading-xs mr-1"></span>
            ) : (
              <SparklesIcon className="h-4 w-4 mr-1" />
            )}
            {aiStep ?? "Auto-detect fields"}
          </Button>
          <Button
            variant="primary"
            disabled={!hasChanges}
            onClick={async () => {
              await onSave(fields, sections);
              markSaved();
            }}
          >
            Save Template
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Toolbar */}
        <div className="w-44 flex-shrink-0 bg-base-100 border-r border-base-300 flex flex-col py-3 gap-0.5 px-2">
          <Tooltip overlay="Select and marquee fields" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("select");
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "select"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <CursorArrowRaysIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Select</span>
              </div>
            </button>
          </Tooltip>
          <Tooltip overlay="Click and drag to pan" placement="right">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setSelectedTool("pan");
                setSelectedFieldIds(new Set());
              }}
              className={cn(
                "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                selectedTool === "pan"
                  ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
              )}
            >
              <div className="flex items-center pl-1">
                <HandRaisedIcon className="h-5 w-5 mr-2" />
                <span className="text-sm">Pan</span>
              </div>
            </button>
          </Tooltip>
          <div className="border-b border-base-300 my-2 mx-2" />
          <p className="text-xs font-medium text-base-content/40 uppercase tracking-wide px-2 mb-2">
            Fields
          </p>
          {Object.entries(FIELD_TYPE_CONFIG).map(([type, config]) => (
            <Tooltip key={type} overlay={config.description} placement="right">
              <button
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (selectedTool === type) {
                    setSelectedTool("select");
                  } else {
                    setSelectedTool(type);
                    setSelectedFieldIds(new Set());
                  }
                }}
                className={cn(
                  "btn border-0 w-full justify-start !min-h-10 shadow-none h-[24px]",
                  selectedTool === type
                    ? "bg-blue-50 dark:bg-blue-900/40 hover:bg-blue-50 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                    : "hover:bg-base-300 text-gray-700 dark:text-base-content/80",
                )}
              >
                <div className="flex items-center pl-1">
                  <span className="mr-2">{config.icon}</span>
                  <span className="text-sm">{config.label}</span>
                </div>
              </button>
            </Tooltip>
          ))}
          <div className="mt-auto px-2 pt-3 border-t border-base-300 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="w-3.5 h-3.5 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                checked={snap.snapEnabled}
                onChange={(e) => snap.setSnapEnabled(e.target.checked)}
              />
              <span className="text-xs text-base-content/60">Snap to guides</span>
            </label>
            <p className="text-xs text-base-content/40">
              Hold{" "}
              <kbd className="px-1 py-0.5 bg-gray-100 dark:bg-base-200 border border-gray-300 dark:border-base-300 rounded text-[10px] font-mono">
                Space
              </kbd>{" "}
              to pan
            </p>
          </div>
        </div>

        {/* PDF Canvas wrapper */}
        <div className="flex-1 min-w-0 relative">
          {/* Floating bulk action bar - outside scroll container so it stays fixed */}
          <div
            className={cn(
              "absolute top-4 left-1/2 -translate-x-1/2 z-30 flex justify-center transition-all duration-200",
              selectedFieldIds.size > 0
                ? "opacity-100 translate-y-0 pointer-events-auto"
                : "opacity-0 -translate-y-2 pointer-events-none",
            )}
          >
            <div className="bg-base-100 shadow-lg border border-base-300 rounded-lg px-4 py-2 flex items-center gap-3">
              <div>
                <span className="text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded inline-flex items-center gap-1">
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                  {selectedFieldIds.size}
                </span>
              </div>
              <div className="w-px h-5 bg-base-300" />
              {(() => {
                const selectedFields = fields.filter((f) =>
                  selectedFieldIds.has(f.id),
                );
                const sectionValues = new Set(
                  selectedFields.map((f) => f.section || ""),
                );
                const commonSection =
                  sectionValues.size === 1 ? [...sectionValues][0] : null;
                return (
                  <select
                    className="h-7 px-2 text-sm border border-base-300 rounded bg-base-100 focus:outline-none max-w-[160px] truncate"
                    value={commonSection === null ? "__mixed__" : commonSection}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "__new__") {
                        setNewSectionName("");
                        setBulkSectionAssign(true);
                        setShowNewSectionModal(true);
                      } else {
                        setFields((prev) =>
                          prev.map((f) =>
                            selectedFieldIds.has(f.id)
                              ? { ...f, section: val || undefined }
                              : f,
                          ),
                        );
                      }
                    }}
                  >
                    {commonSection === null && (
                      <option value="__mixed__" disabled>
                        Assign to section...
                      </option>
                    )}
                    <option value="__new__">+ New section...</option>
                    <option value="">None (remove section)</option>
                    {sections.map((sec) => (
                      <option key={sec.name} value={sec.name}>
                        {sec.name}
                      </option>
                    ))}
                  </select>
                );
              })()}
              {alignment.canAlign && (
                <>
                  <div className="w-px h-5 bg-base-300" />
                  <button
                    onClick={alignment.alignLeft}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align left"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="1" y="1" width="1.5" height="14" rx="0.5" />
                      <rect x="4" y="3" width="10" height="3" rx="0.5" />
                      <rect x="4" y="9" width="6" height="3" rx="0.5" />
                    </svg>
                  </button>
                  <button
                    onClick={alignment.alignCenterH}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align center horizontally"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="7.25" y="1" width="1.5" height="14" rx="0.5" />
                      <rect x="2" y="3" width="12" height="3" rx="0.5" />
                      <rect x="4" y="9" width="8" height="3" rx="0.5" />
                    </svg>
                  </button>
                  <button
                    onClick={alignment.alignRight}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align right"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="13.5" y="1" width="1.5" height="14" rx="0.5" />
                      <rect x="2" y="3" width="10" height="3" rx="0.5" />
                      <rect x="6" y="9" width="6" height="3" rx="0.5" />
                    </svg>
                  </button>
                  <button
                    onClick={alignment.alignTop}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align top"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="1" y="1" width="14" height="1.5" rx="0.5" />
                      <rect x="3" y="4" width="3" height="10" rx="0.5" />
                      <rect x="9" y="4" width="3" height="6" rx="0.5" />
                    </svg>
                  </button>
                  <button
                    onClick={alignment.alignCenterV}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align center vertically"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="1" y="7.25" width="14" height="1.5" rx="0.5" />
                      <rect x="3" y="2" width="3" height="12" rx="0.5" />
                      <rect x="9" y="4" width="3" height="8" rx="0.5" />
                    </svg>
                  </button>
                  <button
                    onClick={alignment.alignBottom}
                    className="p-1.5 text-base-content/40 hover:text-blue-500 dark:hover:text-blue-300 transition-colors"
                    title="Align bottom"
                  >
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <rect x="1" y="13.5" width="14" height="1.5" rx="0.5" />
                      <rect x="3" y="2" width="3" height="10" rx="0.5" />
                      <rect x="9" y="6" width="3" height="6" rx="0.5" />
                    </svg>
                  </button>
                </>
              )}
              <div className="w-px h-5 bg-base-300" />
              <button
                onClick={() => {
                  setFields((prev) =>
                    prev.filter((f) => !selectedFieldIds.has(f.id)),
                  );
                  setSelectedFieldIds(new Set());
                }}
                className="p-1.5 text-base-content/40 hover:text-red-500 transition-colors"
                title="Delete selected fields"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSelectedFieldIds(new Set())}
                className="p-1.5 text-base-content/40 hover:text-gray-600 dark:hover:text-base-content/70 transition-colors"
                title="Clear selection"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div
            ref={pdf.containerRef}
            tabIndex={-1}
            className={cn(
              "h-full overflow-auto bg-base-300 p-8 outline-none",
              pdf.panCursorClass,
            )}
            onMouseDown={handleContainerMouseDown}
            onMouseMove={pdf.handleContainerMouseMove}
            onMouseUp={pdf.handleContainerMouseEnd}
            onMouseLeave={pdf.handleContainerMouseEnd}
          >
            <div
              ref={pdf.canvasRef}
              className={cn(
                "relative mx-auto bg-white shadow-xl select-none", /* paper — stays white in dark mode */
                placingOption ||
                  (selectedTool &&
                    selectedTool !== "select" &&
                    selectedTool !== "pan")
                  ? "cursor-crosshair"
                  : pdf.panCursorClass || "cursor-default",
              )}
              style={{
                width: pdf.pdfDimensions.width * scale || 612,
                height: pdf.pdfDimensions.height * scale || 792,
                transition: pdf.pageReady
                  ? "none"
                  : "width 0.15s ease, height 0.15s ease",
              }}
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={() => {
                if (drawStart) {
                  setDrawStart(null);
                  setDrawCurrent(null);
                }
              }}
            >
              {!pdfFileUrl ? (
                <div className="flex items-center justify-center h-96">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                </div>
              ) : (
              <Document
                file={pdfFileUrl}
                onLoadSuccess={pdf.onDocumentLoadSuccess}
                loading={null}
                error={
                  <div className="flex items-center justify-center h-96 text-error">
                    Failed to load PDF
                  </div>
                }
              >
                <div
                  className="transition-opacity duration-200"
                  style={{ opacity: pdf.pageReady ? 1 : 0 }}
                >
                  <Page
                    pageNumber={pdf.currentPage}
                    scale={scale}
                    onLoadSuccess={pdf.onPageLoadSuccess}
                    onRenderSuccess={() => pdf.setPageReady(true)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={<span />}
                  />
                </div>
              </Document>
              )}

              {/* Loading spinner during page transitions */}
              {!pdf.pageReady && (
                <div className="absolute inset-0 z-30 flex items-center justify-center">
                  <span className="loading loading-spinner loading-md text-base-content/30" />
                </div>
              )}

              {/* Draw preview (field creation) */}
              {pdf.pageReady && drawPreview && (
                <div
                  className="absolute border-2 border-dashed border-primary bg-primary/10 rounded pointer-events-none z-20"
                  style={{
                    left: drawPreview.left,
                    top: drawPreview.top,
                    width: drawPreview.width,
                    height: drawPreview.height,
                  }}
                />
              )}

              {/* Marquee selection rectangle */}
              {pdf.pageReady && marquee.marqueePreview && (
                <div
                  className="absolute border border-blue-500 bg-blue-500/10 pointer-events-none z-20"
                  style={marquee.marqueePreview}
                />
              )}

              {/* Alignment snap guides (drawn while dragging a field) */}
              {pdf.pageReady && (
                <SnapGuideLines guides={snap.guides} scale={scale} />
              )}

              {/* Field overlays — hidden until page is rendered */}
              {pdf.pageReady &&
                currentPageFields.map((field) => {
                  const pos = fieldMovement.getFieldPosition(
                    field.id,
                    field.rect,
                  );
                  const rndHandlers = fieldMovement.getRndHandlers(field.id);

                  return (
                    <Rnd
                      key={field.id}
                      position={pos}
                      size={{
                        width: field.rect.width * scale,
                        height: field.rect.height * scale,
                      }}
                      enableResizing={
                        selectedFieldIds.size === 1 &&
                        selectedFieldIds.has(field.id)
                      }
                      resizeHandleStyles={{
                        top: { height: 4, top: -2 },
                        bottom: { height: 4, bottom: -2 },
                        left: { width: 4, left: -2 },
                        right: { width: 4, right: -2 },
                        topLeft: { width: 6, height: 6, top: -3, left: -3 },
                        topRight: { width: 6, height: 6, top: -3, right: -3 },
                        bottomLeft: {
                          width: 6,
                          height: 6,
                          bottom: -3,
                          left: -3,
                        },
                        bottomRight: {
                          width: 6,
                          height: 6,
                          bottom: -3,
                          right: -3,
                        },
                      }}
                      disableDragging={!selectedFieldIds.has(field.id)}
                      {...rndHandlers}
                      onResizeStop={(
                        e: any,
                        dir: any,
                        ref: HTMLElement,
                        delta: any,
                        position: any,
                      ) => {
                        rndHandlers.onResizeStop(e, dir, ref, delta, position);
                        if (field.type === "checkbox") {
                          localStorage.setItem(
                            "formDefaults:checkboxSize",
                            String(Math.round(ref.offsetWidth / scale)),
                          );
                        }
                      }}
                      bounds="parent"
                      className={cn(
                        "border-2 rounded transition-colors",
                        placingOption && "pointer-events-none",
                        reviewHighlight?.ids.has(field.id)
                          ? "border-green-500 bg-green-200/70 dark:bg-green-700/40 z-10 ring-2 ring-green-300 cursor-pointer"
                          : selectedFieldIds.has(field.id)
                          ? "border-blue-500 bg-blue-500/15 z-10 ring-2 ring-blue-300 cursor-move"
                          : hoveredFieldId === field.id ||
                              hoveredSection ===
                                (field.section || "Uncategorized")
                            ? "border-blue-400 bg-blue-400/15 ring-1 ring-blue-200 cursor-pointer"
                            : "border-primary/50 bg-primary/10 hover:border-primary cursor-pointer",
                      )}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        marquee.handleFieldClick(field.id, e.shiftKey);
                      }}
                    >
                      {editingLabelId === field.id ? (
                        /* paper — stays white in dark mode */
                        <input
                          ref={editingInputRef}
                          autoFocus
                          className="w-full h-full px-1 bg-white text-primary font-medium outline-none border-none"
                          style={{
                            fontSize: `${(field.fontSize || 10) * scale}px`,
                          }}
                          defaultValue={field.label}
                          onBlur={(e) => {
                            const val = e.target.value.trim();
                            if (val)
                              handleFieldUpdate(field.id, { label: val });
                            setEditingLabelId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              (e.target as HTMLInputElement).blur();
                            } else if (e.key === "Escape") {
                              setEditingLabelId(null);
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <div
                          className={cn(
                            "w-full h-full flex text-primary font-medium px-1",
                            field.type === "textarea"
                              ? "items-start pt-0.5 overflow-hidden"
                              : "items-center truncate",
                          )}
                          style={{
                            fontSize: `${(field.fontSize || 10) * scale}px`,
                            ...(field.type === "textarea"
                              ? {
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-wrap",
                                  lineHeight: field.lineHeight || 1.2,
                                }
                              : {}),
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            if (field.type !== "checkbox") {
                              setEditingLabelId(field.id);
                            }
                          }}
                        >
                          {field.type === "checkbox" || (field.type === "circle" && field.optionRects?.length) ? "" : field.label}
                        </div>
                      )}
                      {field.type === "circle" &&
                        (field.optionRects || []).map((r, i) =>
                          r ? (
                            <div
                              key={i}
                              title={field.options?.[i]}
                              className={cn(
                                "absolute rounded-[50%] border-2 border-dashed pointer-events-none",
                                placingOption?.fieldId === field.id && placingOption.index === i
                                  ? "border-amber-500 bg-amber-400/20"
                                  : "border-blue-600 dark:border-blue-300",
                              )}
                              style={{
                                left: (r.x - field.rect.x) * scale - 2,
                                top: (r.y - field.rect.y) * scale - 2,
                                width: r.width * scale + 4,
                                height: r.height * scale + 4,
                              }}
                            />
                          ) : null,
                        )}
                    </Rnd>
                  );
                })}
            </div>
          </div>
          <PdfFloatingNav
            currentPage={pdf.currentPage}
            numPages={pdf.numPages}
            scale={scale}
            onPrevPage={pdf.prevPage}
            onNextPage={pdf.nextPage}
            onZoomIn={pdf.zoomIn}
            onZoomOut={pdf.zoomOut}
            onZoomFit={pdf.zoomFit}
            minScale={0.5}
            maxScale={2}
          />
        </div>

        {/* Right Sidebar - Fields & Properties */}
        <div className="flex-shrink-0 flex flex-row">
          {/* Collapse/Expand toggle bar */}
          <button
            onClick={() => setSidebarCollapsed((v) => !v)}
            className="w-6 flex-shrink-0 border-l border-base-300 bg-base-200 hover:bg-blue-50 dark:hover:bg-blue-900/40 flex items-center justify-center cursor-pointer transition-colors group"
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className="text-[10px] font-medium text-base-content/40 group-hover:text-blue-600 dark:group-hover:text-blue-300 tracking-wider uppercase [writing-mode:vertical-lr] rotate-180 select-none flex items-center gap-1">
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-3 w-3" />
              ) : (
                <ChevronLeftIcon className="h-3 w-3" />
              )}
              {sidebarCollapsed ? "Expand" : "Collapse"}
            </span>
          </button>
          {!sidebarCollapsed && (
            <div
              style={{ width: sidebarPanel.width }}
              className="relative shrink-0 bg-base-100 border-l border-base-300 flex flex-col"
            >
              <ResizeHandle edge="left" active={sidebarPanel.isResizing} {...sidebarPanel.handleProps} />
              {/* Sidebar Tabs */}
              <div className="flex border-b border-base-300">
                <button
                  onClick={() => setSidebarTab("fields")}
                  className={cn(
                    "flex-1 py-2.5 text-sm font-medium text-center transition-colors",
                    sidebarTab === "fields"
                      ? "text-blue-600 dark:text-blue-300 border-b-2 border-blue-600"
                      : "text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80",
                  )}
                >
                  Fields ({fields.length})
                </button>
                <button
                  onClick={() => setSidebarTab("properties")}
                  className={cn(
                    "flex-1 py-2.5 text-sm font-medium text-center transition-colors",
                    sidebarTab === "properties"
                      ? "text-blue-600 dark:text-blue-300 border-b-2 border-blue-600"
                      : "text-base-content/60 hover:text-gray-700 dark:hover:text-base-content/80",
                  )}
                >
                  Properties
                </button>
              </div>

              {sidebarTab === "fields" ? (
                <SidebarFieldList
                  fields={fields}
                  sections={sections}
                  selectedFieldIds={selectedFieldIds}
                  onSelectField={(id) => {
                    const field = fields.find((f) => f.id === id);
                    if (field && field.page !== pdf.currentPage) {
                      pdf.goToPage(field.page);
                    }
                    setSelectedFieldIds(new Set([id]));
                    setSidebarTab("properties");
                  }}
                  onToggleField={(id) => {
                    const field = fields.find((f) => f.id === id);
                    if (field && field.page !== pdf.currentPage) {
                      pdf.goToPage(field.page);
                    }
                    setSelectedFieldIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(id)) next.delete(id);
                      else next.add(id);
                      return next;
                    });
                  }}
                  onDeleteField={handleDeleteField}
                  onUpdateField={handleFieldUpdate}
                  onBulkUpdateSection={(fieldIds, section) => {
                    setFields((prev) =>
                      reorderBySections(
                        prev.map((f) =>
                          fieldIds.has(f.id) ? { ...f, section } : f,
                        ),
                      ),
                    );
                  }}
                  dragFieldId={dragFieldId}
                  setDragFieldId={setDragFieldId}
                  dragOverSection={dragOverSection}
                  setDragOverSection={setDragOverSection}
                  onAddSection={() => {
                    setNewSectionName("");
                    setShowNewSectionModal(true);
                  }}
                  onRemoveSection={(section) => {
                    const hasFields = fields.some((f) => f.section === section);
                    if (hasFields) {
                      setSectionToDelete(section);
                      return;
                    }
                    setSections((prev) =>
                      prev.filter((s) => s.name !== section),
                    );
                  }}
                  onUpdateSectionContext={(sectionName, context) => {
                    setSections((prev) =>
                      prev.map((s) =>
                        s.name === sectionName ? { ...s, context } : s,
                      ),
                    );
                  }}
                  onReorderFieldInSection={(fieldId, section, direction) => {
                    setFields((prev) => {
                      const sectionKey =
                        section === "Uncategorized" ? undefined : section;
                      const sectionFields = prev
                        .filter((f) => (f.section || undefined) === sectionKey)
                        .sort((a, b) => (a.order || 0) - (b.order || 0));
                      const idx = sectionFields.findIndex(
                        (f) => f.id === fieldId,
                      );
                      if (idx < 0) return prev;
                      const swapIdx = direction === "up" ? idx - 1 : idx + 1;
                      if (swapIdx < 0 || swapIdx >= sectionFields.length)
                        return prev;
                      const orderA = sectionFields[idx].order;
                      const orderB = sectionFields[swapIdx].order;
                      return prev.map((f) => {
                        if (f.id === sectionFields[idx].id)
                          return { ...f, order: orderB };
                        if (f.id === sectionFields[swapIdx].id)
                          return { ...f, order: orderA };
                        return f;
                      });
                    });
                  }}
                  hoveredFieldId={hoveredFieldId}
                  onHoverField={setHoveredFieldId}
                  hoveredSection={hoveredSection}
                  onHoverSection={setHoveredSection}
                  currentPage={pdf.currentPage}
                  onNavigateToPage={(page) => {
                    if (page !== pdf.currentPage) {
                      pdf.goToPage(page);
                    }
                  }}
                  collapsedSections={collapsedSections}
                  highlightedFieldIds={reviewHighlight?.ids}
                  highlightedSection={reviewHighlight?.section}
                  onToggleCollapse={(section) => {
                    setCollapsedSections((prev) => {
                      const next = new Set(prev);
                      if (next.has(section)) next.delete(section);
                      else next.add(section);
                      return next;
                    });
                  }}
                  onReorderSections={(fromIdx, toIdx) => {
                    setSections((prev) => {
                      const next = [...prev];
                      const [moved] = next.splice(fromIdx, 1);
                      next.splice(toIdx, 0, moved);
                      return next;
                    });
                  }}
                  onDropFieldAtPosition={(
                    fieldId,
                    targetSection,
                    targetIndex,
                  ) => {
                    setFields((prev) => {
                      const sectionKey =
                        targetSection === "Uncategorized"
                          ? undefined
                          : targetSection;
                      // Move field to target section
                      const updated = prev.map((f) =>
                        f.id === fieldId ? { ...f, section: sectionKey } : f,
                      );
                      // Group all fields by section, sorted by current order
                      const grouped: Record<string, FormFieldDefinition[]> = {};
                      for (const f of updated) {
                        const sec = f.section || "";
                        if (!grouped[sec]) grouped[sec] = [];
                        if (f.id !== fieldId) grouped[sec].push(f);
                      }
                      // Sort each group by existing order
                      for (const sec in grouped) {
                        grouped[sec].sort(
                          (a, b) => (a.order || 0) - (b.order || 0),
                        );
                      }
                      // Insert the moved field at the target index in its new section
                      const movedField = updated.find((f) => f.id === fieldId)!;
                      const targetKey = sectionKey || "";
                      if (!grouped[targetKey]) grouped[targetKey] = [];
                      grouped[targetKey].splice(targetIndex, 0, movedField);
                      // Flatten back into array (section order preserved) and reorder
                      const reordered = Object.values(grouped).flat();
                      return reorderBySections(reordered);
                    });
                  }}
                />
              ) : selectedFieldIds.size > 1 ? (
                /* Bulk actions for multi-select */
                <div className="p-4 space-y-4 overflow-auto flex-1">
                  <p className="text-sm font-medium text-gray-700 dark:text-base-content/80">
                    {selectedFieldIds.size} fields selected
                  </p>

                  <div className="form-control">
                    <div className="flex items-center pb-1">
                      <span className="font-medium text-sm">
                        Assign Section
                      </span>
                      <TooltipIcon
                        description="Assign all selected fields to a section at once."
                        size="sm"
                      />
                    </div>
                    {(() => {
                      const selectedFields = fields.filter((f) =>
                        selectedFieldIds.has(f.id),
                      );
                      const sectionValues = new Set(
                        selectedFields.map((f) => f.section || ""),
                      );
                      const commonSection =
                        sectionValues.size === 1 ? [...sectionValues][0] : null;
                      return (
                        <select
                          className="w-full h-8 px-3 text-sm border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors"
                          value={
                            commonSection === null ? "__mixed__" : commonSection
                          }
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "__new__") {
                              setNewSectionName("");
                              setBulkSectionAssign(true);
                              setShowNewSectionModal(true);
                            } else {
                              setFields((prev) =>
                                reorderBySections(
                                  prev.map((f) =>
                                    selectedFieldIds.has(f.id)
                                      ? { ...f, section: val || undefined }
                                      : f,
                                  ),
                                ),
                              );
                            }
                          }}
                        >
                          {commonSection === null && (
                            <option value="__mixed__" disabled>
                              Choose section...
                            </option>
                          )}
                          <option value="__new__">+ New section...</option>
                          <option value="">None (remove section)</option>
                          {sections.map((sec) => (
                            <option key={sec.name} value={sec.name}>
                              {sec.name}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>

                  <div className="form-control">
                    <label className="cursor-pointer flex items-center gap-2">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                        checked={fields
                          .filter((f) => selectedFieldIds.has(f.id))
                          .every((f) => f.required)}
                        onChange={(e) => {
                          const required = e.target.checked;
                          setFields((prev) =>
                            prev.map((f) =>
                              selectedFieldIds.has(f.id)
                                ? { ...f, required }
                                : f,
                            ),
                          );
                        }}
                      />
                      <span className="text-sm text-gray-700 dark:text-base-content/80">
                        Mark all as required
                      </span>
                    </label>
                  </div>

                  <div className="pt-4 border-t border-base-300">
                    <button
                      onClick={() => {
                        setFields((prev) =>
                          prev.filter((f) => !selectedFieldIds.has(f.id)),
                        );
                        setSelectedFieldIds(new Set());
                        setSidebarTab("fields");
                      }}
                      className="w-full py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete {selectedFieldIds.size} Fields
                    </button>
                  </div>
                </div>
              ) : selectedField ? (
                <div className="p-4 space-y-4 overflow-auto flex-1">
                  <div className="form-control">
                    <div className="flex items-center pb-1">
                      <span className="font-medium text-sm">Field Name</span>
                      <TooltipIcon
                        description="The display name for this field. The AI uses this to understand what information belongs here."
                        size="sm"
                      />
                    </div>
                    <Input
                      variant="sm"
                      className="w-full"
                      value={selectedField.label}
                      onChange={(e) => {
                        const label = (e.target as HTMLInputElement).value;
                        const name = label
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, "_")
                          .replace(/^_|_$/g, "");
                        handleFieldUpdate(selectedField.id, { label, name });
                      }}
                    />
                  </div>

                  <div className="form-control">
                    <div className="flex items-center pb-1">
                      <span className="font-medium text-sm">Type</span>
                      <TooltipIcon
                        description="The kind of input this field accepts. Choose the type that best matches how the field appears on the original form."
                        size="sm"
                      />
                    </div>
                    <select
                      className="w-full h-8 px-3 text-sm border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors"
                      value={
                        selectedField.type === "textarea"
                          ? "text"
                          : selectedField.type
                      }
                      onChange={(e) =>
                        handleFieldUpdate(selectedField.id, {
                          type: e.target.value as any,
                        })
                      }
                    >
                      {Object.entries(FIELD_TYPE_CONFIG).map(
                        ([type, config]) => (
                          <option key={type} value={type}>
                            {config.label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>

                  {(selectedField.type === "text" ||
                    selectedField.type === "textarea") && (
                    <div className="form-control">
                      <label className="cursor-pointer flex items-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                          checked={selectedField.type === "textarea"}
                          onChange={(e) => {
                            const newType = e.target.checked
                              ? "textarea"
                              : "text";
                            localStorage.setItem(
                              "formDefaults:multiline",
                              String(e.target.checked),
                            );
                            handleFieldUpdate(selectedField.id, {
                              type: newType,
                            });
                          }}
                        />
                        <span className="text-sm text-gray-700 dark:text-base-content/80 pl-2">
                          Multi-line
                        </span>
                        <TooltipIcon
                          description="Enable for fields that need multiple lines of text, like narratives or descriptions. Disabled for short fields like names or badge numbers."
                          size="sm"
                        />
                      </label>
                    </div>
                  )}

                  {selectedField.type === "circle" && (
                    <CircleOptionsEditor
                      field={selectedField}
                      placing={placingOption}
                      onChange={(updates) => handleFieldUpdate(selectedField.id, updates)}
                      onPlace={(index, single) => {
                        setSelectedTool("select");
                        pdf.goToPage(selectedField.page);
                        setPlacingOption({ fieldId: selectedField.id, index, single });
                      }}
                      onStopPlacing={() => setPlacingOption(null)}
                    />
                  )}

                  <div className="form-control">
                    <div className="flex items-center pb-1">
                      <span className="font-medium text-sm">Section</span>
                      <TooltipIcon
                        description="Group related fields together under a section. Sections help organize fields by the question or area of the form they belong to."
                        size="sm"
                      />
                    </div>
                    <select
                      className="w-full h-8 px-3 text-sm border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-gray-400 dark:focus:border-base-content/30 transition-colors"
                      value={selectedField.section || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "__new__") {
                          e.target.value = selectedField.section || "";
                          setNewSectionName("");
                          setShowNewSectionModal(true);
                        } else {
                          handleFieldUpdate(selectedField.id, {
                            section: val || undefined,
                          });
                        }
                      }}
                    >
                      <option value="">None</option>
                      {sections.map((sec) => (
                        <option key={sec.name} value={sec.name}>
                          {sec.name}
                        </option>
                      ))}
                      <option value="__new__">+ Add new section...</option>
                    </select>
                  </div>

                  <TextArea
                    label={
                      <div className="flex items-center">
                        <span>Additional Context</span>
                        <TooltipIcon
                          description="Instructions that help the AI understand what information belongs in this field. Be specific — e.g., 'The reporting officer's full legal name as it appears on department records.'"
                          size="sm"
                        />
                      </div>
                    }
                    labelSize="sm"
                    rows={5}
                    value={selectedField.additionalContext || ""}
                    onChange={(e) =>
                      handleFieldUpdate(selectedField.id, {
                        additionalContext: e.target.value || undefined,
                      })
                    }
                    placeholder="e.g., Officer's full name and badge number as it appears on department records"
                  />

                  {selectedField.type !== "checkbox" && selectedField.type !== "circle" && (
                    <div className="form-control">
                      <div className="flex items-center pb-1">
                        <span className="font-medium text-sm">Font Size</span>
                        <TooltipIcon
                          description="The font size (in points) used when rendering this field's value on the filled PDF."
                          size="sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const current =
                              selectedField.fontSize ||
                              (selectedField.type === "textarea" ? 9 : 10);
                            if (current > 4) {
                              const next = current - 1;
                              handleFieldUpdate(selectedField.id, {
                                fontSize: next,
                              });
                              localStorage.setItem(
                                "formDefaults:fontSize",
                                String(next),
                              );
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-14 h-7 px-2 text-sm text-center border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors"
                          value={
                            selectedField.fontSize ||
                            (selectedField.type === "textarea" ? 9 : 10)
                          }
                          onChange={(e) => {
                            const val = parseInt(e.target.value, 10);
                            if (!isNaN(val) && val >= 4 && val <= 72) {
                              handleFieldUpdate(selectedField.id, {
                                fontSize: val,
                              });
                              localStorage.setItem(
                                "formDefaults:fontSize",
                                String(val),
                              );
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            const current =
                              selectedField.fontSize ||
                              (selectedField.type === "textarea" ? 9 : 10);
                            if (current < 72) {
                              const next = current + 1;
                              handleFieldUpdate(selectedField.id, {
                                fontSize: next,
                              });
                              localStorage.setItem(
                                "formDefaults:fontSize",
                                String(next),
                              );
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        >
                          +
                        </button>
                        <span className="text-xs text-base-content/40">px</span>
                      </div>
                    </div>
                  )}

                  {selectedField.type === "textarea" && (
                    <div className="form-control">
                      <div className="flex items-center pb-1">
                        <span className="font-medium text-sm">Line Spacing</span>
                        <TooltipIcon
                          description="Line spacing multiplier (line height ÷ font size). 1.0 is tight, 1.2 is normal, 1.5 is loose. Only affects how multi-line text wraps in the filled PDF."
                          size="sm"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const current = selectedField.lineHeight || 1.2;
                            const next = Math.round((current - 0.1) * 10) / 10;
                            if (next >= 0.8) {
                              handleFieldUpdate(selectedField.id, {
                                lineHeight: next,
                              });
                              localStorage.setItem(
                                "formDefaults:lineHeight",
                                String(next),
                              );
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        >
                          -
                        </button>
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-14 h-7 px-2 text-sm text-center border border-base-300 rounded-md bg-base-100 focus:outline-none focus:border-blue-400 transition-colors"
                          value={selectedField.lineHeight || 1.2}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0.8 && val <= 3) {
                              handleFieldUpdate(selectedField.id, {
                                lineHeight: val,
                              });
                              localStorage.setItem(
                                "formDefaults:lineHeight",
                                String(val),
                              );
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            const current = selectedField.lineHeight || 1.2;
                            const next = Math.round((current + 0.1) * 10) / 10;
                            if (next <= 3) {
                              handleFieldUpdate(selectedField.id, {
                                lineHeight: next,
                              });
                              localStorage.setItem(
                                "formDefaults:lineHeight",
                                String(next),
                              );
                            }
                          }}
                          className="w-7 h-7 flex items-center justify-center border border-base-300 rounded text-gray-600 dark:text-base-content/70 hover:bg-base-200 transition-colors text-sm font-medium"
                        >
                          +
                        </button>
                        <span className="text-xs text-base-content/40">×</span>
                      </div>
                    </div>
                  )}

                  <div className="form-control">
                    <label className="cursor-pointer flex items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-blue-600 dark:text-blue-300 border-gray-300 dark:border-base-300 rounded focus:ring-blue-500"
                        checked={selectedField.required}
                        onChange={(e) =>
                          handleFieldUpdate(selectedField.id, {
                            required: e.target.checked,
                          })
                        }
                      />
                      <span className="text-sm text-gray-700 dark:text-base-content/80 pl-2">
                        Required field
                      </span>
                      <TooltipIcon
                        description="When enabled, the AI must fill this field before the form can be submitted. Use for fields that are mandatory on the original form."
                        size="sm"
                      />
                    </label>
                  </div>

                  <div className="pt-4 border-t border-base-300">
                    <button
                      onClick={() => {
                        handleDeleteField(selectedField.id);
                        setSidebarTab("fields");
                      }}
                      className="w-full py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg transition-colors flex items-center justify-center gap-1"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete Field
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-4 text-center text-base-content/40">
                  <p>Select a field to edit its properties</p>
                  <p className="text-xs mt-2">
                    Or select a field type from the toolbar and click on the PDF
                    to place it
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {/* New Section Modal */}
      <dialog className={`modal ${showNewSectionModal ? "modal-open" : ""}`}>
        <div className="modal-box max-w-2xl">
          <h3 className="font-bold text-lg mt-2">
            Enter a name for the new section.
          </h3>
          <p className="pb-4 text-gray-600 dark:text-base-content/70 text-sm">
            Sections are used to group inputs together and help the AI
            understand the structure of the form.
          </p>
          <Input
            title="Section Name"
            variant="md"
            value={newSectionName}
            onChange={(e) =>
              setNewSectionName((e.target as HTMLInputElement).value)
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" && newSectionName.trim()) {
                const name = newSectionName.trim();
                if (!sections.some((s) => s.name === name)) {
                  setSections((prev) => [
                    ...prev,
                    { name, context: newSectionContext.trim() || undefined },
                  ]);
                }
                if (bulkSectionAssign && selectedFieldIds.size > 0) {
                  setFields((prev) =>
                    prev.map((f) =>
                      selectedFieldIds.has(f.id) ? { ...f, section: name } : f,
                    ),
                  );
                  setBulkSectionAssign(false);
                } else if (primarySelectedId) {
                  handleFieldUpdate(primarySelectedId, { section: name });
                }
                setShowNewSectionModal(false);
                setNewSectionName("");
                setNewSectionContext("");
              }
            }}
            placeholder="e.g., Perceived threat leading to use of force"
            autoFocus
            className="!max-w-full w-full"
          />
          <TextArea
            label="AI Context"
            labelSize="sm"
            rows={3}
            value={newSectionContext}
            onChange={(e) => setNewSectionContext(e.target.value)}
            placeholder="e.g., Extract details about the type of force used, duration, and justification for use of force"
            className="mt-1"
          />
          <div className="modal-action mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowNewSectionModal(false);
                setNewSectionName("");
                setNewSectionContext("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const name = newSectionName.trim();
                if (name) {
                  if (!sections.some((s) => s.name === name)) {
                    setSections((prev) => [
                      ...prev,
                      { name, context: newSectionContext.trim() || undefined },
                    ]);
                  }
                  if (bulkSectionAssign && selectedFieldIds.size > 0) {
                    setFields((prev) =>
                      prev.map((f) =>
                        selectedFieldIds.has(f.id)
                          ? { ...f, section: name }
                          : f,
                      ),
                    );
                    setBulkSectionAssign(false);
                  } else if (primarySelectedId) {
                    handleFieldUpdate(primarySelectedId, { section: name });
                  }
                }
                setShowNewSectionModal(false);
                setNewSectionName("");
                setNewSectionContext("");
              }}
              disabled={!newSectionName.trim()}
            >
              Add Section
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button
            onClick={() => {
              setShowNewSectionModal(false);
              setNewSectionName("");
              setNewSectionContext("");
            }}
          >
            close
          </button>
        </form>
      </dialog>

      {placingOption && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-[9990] rounded-full bg-amber-500 text-white text-sm font-medium px-4 py-2 shadow-lg pointer-events-none">
          {(() => {
            const f = fields.find((x) => x.id === placingOption.fieldId);
            const total = f?.options?.length ?? 0;
            const word = f?.options?.[placingOption.index] || "this option";
            return `Drag a box around "${word}" on the form (${placingOption.index + 1} of ${total}). Esc to stop.`;
          })()}
        </div>
      )}

      {review && (
        <AiFieldReview
          portalContainer={portalContainer}
          groups={review.groups}
          onPreview={previewReviewStep}
          onFinish={finishReview}
          onDiscardAll={discardReview}
          onWheelPassthrough={(e) => pdf.containerRef.current?.scrollBy({ top: e.deltaY, left: e.deltaX })}
        />
      )}

      {/* Confirm before auto-detect on a form that already has fields */}
      <dialog className={`modal ${showAiConfirm ? "modal-open" : ""}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">Auto-detect fields?</h3>
          <p className="py-3 text-sm text-base-content/70">
            {fields.length > 0
              ? `This form already has ${fields.length} field${fields.length === 1 ? "" : "s"}.`
              : "This form has no fields yet."}{" "}
            The AI will read it in the
            background and suggest new fields, groupings, labels and types. The form is locked until it finishes (you
            can leave and come back), and nothing changes until you accept each suggestion.
          </p>
          <ReferencePdfPicker file={aiReferenceFile} onChange={setAiReferenceFile} />
          <div className="modal-action">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAiConfirm(false);
                setAiReferenceFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setShowAiConfirm(false);
                void runAutoDetect();
              }}
            >
              Review suggestions
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setShowAiConfirm(false)}>close</button>
        </form>
      </dialog>

      {/* Delete section confirmation modal */}
      <dialog className={`modal ${sectionToDelete ? "modal-open" : ""}`}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">Delete Section</h3>
          <p className="py-4">
            Are you sure you want to delete "{sectionToDelete}"? This will also
            delete{" "}
            <span className="font-semibold text-error">
              {fields.filter((f) => f.section === sectionToDelete).length} field
              {fields.filter((f) => f.section === sectionToDelete).length === 1
                ? ""
                : "s"}
            </span>{" "}
            inside it.
          </p>
          <div className="modal-action">
            <Button variant="outline" onClick={() => setSectionToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!sectionToDelete) return;
                setSections((prev) =>
                  prev.filter((s) => s.name !== sectionToDelete),
                );
                setFields((prev) =>
                  reorderBySections(
                    prev.filter((f) => f.section !== sectionToDelete),
                  ),
                );
                setSectionToDelete(null);
              }}
            >
              Delete Section & Fields
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setSectionToDelete(null)}>close</button>
        </form>
      </dialog>
    </div>
  );
};

export default FormDesigner;
